import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import ShowToast from "components/ShowToast";
import { fetchWrapper } from "utils/apiFetch";

// Calls the single bulk endpoint (SoftMintAPI.BulkWalletSettlementTransaction
// @ soft_bulk_payout_transaction), which runs the exact same per-row logic
// as the single "Settle Now" flow (WalletSettlementTransaction @
// soft_payout_transaction) but inside one request/loop server-side, with
// fresh locked wallet balances read between rows. See bulk_wallet_settlement_view.py.

const REQUIRED_HEADERS = [
  "Beneficiary Name",
  "Account Number",
  "IFSC Code",
  "Bank Name",
  "Amount",
];

const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const MIN_AMOUNT = 101;
const MAX_AMOUNT = 50000;

const emptyRowState = () => ({
  rows: [],
  fileName: "",
});

const BulkPayoutModal = ({ show, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [{ rows, fileName }, setState] = useState(emptyRowState());
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [transectionMode, setTransectionMode] = useState("IMPS");
  const [pipe, setPipe] = useState("bank1");

  if (!show) return null;

  const resetAndClose = () => {
    setState(emptyRowState());
    setProgress({ done: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const downloadSample = () => {
    const sampleRows = [
      ["S.No", "Beneficiary Name", "Account Number", "IFSC Code", "Bank Name", "Amount"],
      [1, "Ramesh Kumar", "123456789012", "SBIN0001234", "State Bank of India", 5000],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleRows);
    ws["!cols"] = [{ wch: 8 }, { wch: 26 }, { wch: 20 }, { wch: 16 }, { wch: 26 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Payout");
    XLSX.writeFile(wb, "bulk_payout_sample.xlsx");
  };

  const validateRow = (raw, index) => {
    const errors = [];
    const name = String(raw["Beneficiary Name"] ?? "").trim();
    const account = String(raw["Account Number"] ?? "").trim();
    const ifsc = String(raw["IFSC Code"] ?? "").trim().toUpperCase();
    const bank = String(raw["Bank Name"] ?? "").trim();
    const amountRaw = raw["Amount"];
    const amount = Number(amountRaw);

    if (!name) errors.push("Beneficiary name is required");
    if (!account || !/^\d{6,20}$/.test(account)) errors.push("Account number looks invalid");
    if (!ifsc || !IFSC_REGEX.test(ifsc)) errors.push("IFSC code looks invalid");
    if (!bank) errors.push("Bank name is required");
    if (!amountRaw || Number.isNaN(amount)) errors.push("Amount is required");
    else if (amount < MIN_AMOUNT) errors.push(`Amount must be at least ₹${MIN_AMOUNT}`);
    else if (amount > MAX_AMOUNT) errors.push(`Amount cannot exceed ₹${MAX_AMOUNT}`);

    return {
      sno: index + 1,
      name,
      account,
      ifsc,
      bank,
      amount: Number.isNaN(amount) ? 0 : amount,
      errors,
      status: "pending", // pending | success | failed
      message: "",
      referenceNum: "",
    };
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const missing = REQUIRED_HEADERS.filter(
        (h) => !Object.prototype.hasOwnProperty.call(json[0] || {}, h)
      );
      if (json.length === 0 || missing.length) {
        ShowToast(
          json.length === 0
            ? "The file has no data rows"
            : `Missing column(s): ${missing.join(", ")}`,
          "danger"
        );
        setState(emptyRowState());
        return;
      }

      const parsedRows = json.map((raw, i) => validateRow(raw, i));
      setState({ rows: parsedRows, fileName: file.name });
    } catch (err) {
      ShowToast("Could not read that file. Please upload a valid .xlsx/.csv", "danger");
      setState(emptyRowState());
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeRow = (sno) => {
    setState((prev) => ({ ...prev, rows: prev.rows.filter((r) => r.sno !== sno) }));
  };

  const validCount = rows.filter((r) => r.errors.length === 0).length;
  const invalidCount = rows.length - validCount;

  const submitBulk = async () => {
    const toSubmit = rows.filter((r) => r.errors.length === 0);
    if (toSubmit.length === 0) {
      ShowToast("No valid rows to submit", "danger");
      return;
    }

    setSubmitting(true);
    setProgress({ done: 0, total: toSubmit.length });

    let lat = "";
    let long = "";
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 })
      );
      lat = String(pos.coords.latitude);
      long = String(pos.coords.longitude);
    } catch {
      // location is best-effort; backend still accepts blank lat/long
    }

    let successCount = 0;
    let failCount = 0;

    try {
      const payload = {
        transection_mode: transectionMode,
        pipe: pipe,
        lat,
        long,
        payouts: toSubmit.map((row) => ({
          account_number: row.account,
          ifsc_code: row.ifsc,
          holder_name: row.name,
          amount: row.amount,
          remark: `Bulk payout - ${row.name}`,
          pipe: pipe,
        })),
      };

      // mark all rows as submitting up front since this is now one request
      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((r) =>
          toSubmit.some((t) => t.sno === r.sno) ? { ...r, status: "submitting" } : r
        ),
      }));

      const res = await fetchWrapper("https://pro.mokshiq.in/soft_bulk_payout_transaction", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const results = res?.results || [];
      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => {
          const submittedIdx = toSubmit.findIndex((t) => t.sno === r.sno);
          if (submittedIdx === -1) return r;
          const rowResult = results[submittedIdx];
          if (!rowResult) return { ...r, status: "failed", message: "No result returned" };
          const ok = rowResult.status === true;
          if (ok) successCount += 1;
          else failCount += 1;
          return {
            ...r,
            status: ok ? "success" : "failed",
            message: rowResult.message || (ok ? "Payout accepted" : "Payout failed"),
            referenceNum: rowResult.reference_num || "",
          };
        }),
      }));
      setProgress({ done: toSubmit.length, total: toSubmit.length });
    } catch (err) {
      failCount = toSubmit.length;
      setState((prev) => ({
        ...prev,
        rows: prev.rows.map((r) =>
          toSubmit.some((t) => t.sno === r.sno)
            ? { ...r, status: "failed", message: "Network / server error" }
            : r
        ),
      }));
    }

    setSubmitting(false);
    ShowToast(
      `Bulk payout finished: ${successCount} succeeded, ${failCount} failed`,
      failCount ? "warning" : "success"
    );
    if (successCount && onSuccess) onSuccess();
  };

  return (
    <div className="modal-backdrop-custom" role="dialog" aria-modal="true">
      <div className="modal-panel bulk-payout-modal">
        <div className="modal-header">
          <h5>Bulk Payout</h5>
          <button
            type="button"
            className="icon-btn"
            onClick={resetAndClose}
            aria-label="Close"
          >
            <i className="ph-bold ph-x" />
          </button>
        </div>

        <div className="modal-body">
          <div className="bulk-toolbar">
            <button className="btn outline" onClick={downloadSample} type="button">
              <i className="ph-duotone ph-download-simple" /> Download sample template
            </button>

            <label className="btn primary upload-btn">
              <i className="ph-duotone ph-upload-simple" /> Upload Excel / CSV
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFile}
                hidden
              />
            </label>

            <div className="mode-select">
              <label>Mode</label>
              <select value={transectionMode} onChange={(e) => setTransectionMode(e.target.value)}>
                <option value="IMPS">IMPS</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
              </select>
            </div>

            <div className="mode-select">
              <label>Pipe</label>
              <select value={pipe} onChange={(e) => setPipe(e.target.value)}>
                <option value="bank1">Bank 1</option>
                <option value="bank2">Bank 2</option>
                <option value="bank3">Bank 3</option>
              </select>
            </div>
          </div>

          {parsing && <div className="loader">Reading file...</div>}

          {fileName && !parsing && (
            <>
              <div className="bulk-summary">
                <span>{fileName}</span>
                <span className="pill valid">{validCount} valid</span>
                {invalidCount > 0 && <span className="pill invalid">{invalidCount} need fixing</span>}
              </div>

              <div className="bulk-table-wrap">
                <table className="bulk-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Beneficiary</th>
                      <th>Account No</th>
                      <th>IFSC</th>
                      <th>Bank</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.sno} className={r.errors.length ? "row-invalid" : ""}>
                        <td>{r.sno}</td>
                        <td>{r.name}</td>
                        <td>{r.account}</td>
                        <td>{r.ifsc}</td>
                        <td>{r.bank}</td>
                        <td>₹{r.amount || "-"}</td>
                        <td>
                          {r.errors.length > 0 && (
                            <span className="status-badge danger" title={r.errors.join("; ")}>
                              Fix: {r.errors[0]}
                            </span>
                          )}
                          {r.errors.length === 0 && r.status === "pending" && (
                            <span className="status-badge muted">Ready</span>
                          )}
                          {r.status === "submitting" && (
                            <span className="status-badge info">Submitting…</span>
                          )}
                          {r.status === "success" && (
                            <span className="status-badge success" title={r.referenceNum}>
                              Success
                            </span>
                          )}
                          {r.status === "failed" && (
                            <span className="status-badge danger" title={r.message}>
                              {r.message || "Failed"}
                            </span>
                          )}
                        </td>
                        <td>
                          {r.status === "pending" && (
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => removeRow(r.sno)}
                            >
                              <i className="ph-bold ph-trash" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {submitting && (
            <span className="progress-text">
              Processing {progress.done}/{progress.total}…
            </span>
          )}
          <button
            type="button"
            className="btn outline"
            onClick={resetAndClose}
            disabled={submitting}
          >
            Close
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={submitBulk}
            disabled={submitting || validCount === 0}
          >
            {submitting ? "Processing..." : `Submit ${validCount || ""} Payout${validCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkPayoutModal;